import { useMemo, useRef } from "react";

import { ChartDataset } from "chart.js";

import { postTrackingData } from "@/api/endpoints/track";
import Line from "@/components/Charts/Line";
import { ANALYSIS_DATA_TYPE, TimePeriod } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import ClickHandlerPlugin from "@components/Charts/plugins/ClickHandlerPlugin";
import IndexPlugin from "@components/Charts/plugins/IndexPlugin";
import ReactLegendPlugin from "@components/Charts/plugins/ReactLegendPlugin";
import { dynamicIntervalLabels } from "@components/Charts/utils/dynamicIntervalLabels";
import prepareDatasets from "@components/Charts/utils/prepareDataset";

import { BaseWidget } from "../BaseWidget";
import CheckboxLegend from "./partials/CheckboxLegend";
import {
  reactLegendOnClickForSoundWithIndex,
  tooltipWithDateRangeCallback,
} from "./utils";
import { WidgetProps } from "./WidgetProps";

type MentionsOverTimeData = {
  results: {
    labels: string[];
    dataset: ChartDataset<"line", number[]>[];
  };
  meta: { time_period: TimePeriod };
}[];

const MentionsOverTime: React.FunctionComponent<
  WidgetProps<MentionsOverTimeData>
> = (props) => {
  const { data, filters } = props;

  const chartData = useMemo(
    () =>
      data?.length === 1
        ? prepareDatasets({
            datasets: data[0].results.dataset || [],
            labels: data[0].results.labels || [],
          })
        : undefined,
    [data]
  );
  const legendRef = useRef<HTMLDivElement>(null);

  const { formatDate } = useI18n();

  const hasData = data?.some(({ results }) =>
    results?.dataset?.some(({ data }) => data?.some((d: number) => d > 0))
  );

  return (
    <BaseWidget
      // The types here should be fixed within BaseWidget. That is out of scope for now.
      hasData={hasData}
      toolRef={legendRef}
      {...props}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_NUM_MENTIONS_LINE_INSIGHTS}
    >
      {chartData && (
        <Line
          options={{
            plugins: {
              click: {
                onClick: (appliedFilters) => {
                  postTrackingData(EVENT.WIDGET_CLICK, {
                    widget: props.metric,
                  });
                  props.fetchMentions_v2(appliedFilters);
                },
              },
              tooltip: {
                callbacks: {
                  title: tooltipWithDateRangeCallback(
                    chartData.labels,
                    data?.[0]?.meta?.time_period,
                    formatDate,
                    filters?.end_date
                  ),
                },
              },
              legend: {
                display: false,
              },
              indexPlugin: {
                type: ANALYSIS_DATA_TYPE.INDUSTRY_INDEX,
              },
              reactLegend: {
                container: legendRef,
                component: CheckboxLegend,
                hidden: [ANALYSIS_DATA_TYPE.INDUSTRY_INDEX],
                onClick: reactLegendOnClickForSoundWithIndex,
              },
            },
            scales: {
              y: {
                ticks: {
                  precision: 0, // forces integers
                },
                min: 0,
              },
              // scale for grid lines and actual data. These labels are hidden
              x: {
                type: "time",
                ticks: {
                  display: false,
                  source: "labels",
                },
                grid: {
                  drawTicks: true,
                },
                time: {
                  minUnit: "month",
                  tooltipFormat: "PP",
                },
              },
              // scale to display months labels
              x2: dynamicIntervalLabels(data?.[0]?.meta?.time_period),
            },
          }}
          data={chartData}
          plugins={[IndexPlugin, ReactLegendPlugin, ClickHandlerPlugin]}
        />
      )}
    </BaseWidget>
  );
};

export default MentionsOverTime;
